import React from "react"
import BotIcon from "../assets/botIcon"
import CoffeeIcon from "../assets/coffeeIcon"
import CollabIcon from "../assets/Collab"
import CrownIcon from "../assets/crownIcon"
import DrunkStartupIcon from "../assets/DrunkStartupsIcon"
import {
  CharadesWhiteIcon,
  DoddleIcon,
  TriviaIcon,
  TtalIcon,
  WYRIcon,
} from "../assets/heroVideoOptions"
import KytIconWhite from "../assets/KytIconWhite"
import MtIconWhite from "../assets/MtIconWhite"
import RIghtArrow2 from "../assets/RightArrow2"
import SmileIcon from "../assets/SmileIcon"
import SpeedIconWhite from "../assets/SpeedIconWhite"
import UsersIcon from "../assets/usersIcon"
import { useOnScreen, useWindowSize } from "../hooks"
import MixerCard, { IMixerCard } from "./MixerCard"

export const MIXERS: IMixerCard[] = [
  {
    name: "Drunk Startups",
    description: `Ideate with your team on some crazy ideas. Let’s see who gets funded.`,
    tags: [
      {
        name: "Collaborative",
        icon: <CollabIcon />,
      },
      { name: "Team building", icon: <UsersIcon color="var(--theme-black)" /> },
    ],
    color: "--theme-yellow",
    textColor: "var(--theme-black)",
    icon: <DrunkStartupIcon color="var(--theme-black)" />,
    theme: "BLACK",
    templateCode: "5a063bf5-387c-48c9-bf45-d47d69d7a313",
  },
  {
    name: "Mindful talks",
    description: `From appreciation to inspiration, choose a topic and put your heads together.`,
    tags: [
      { name: "Team building", icon: <UsersIcon /> },
      { name: "Casual", icon: <CoffeeIcon /> },
    ],
    color: "--theme-green",
    textColor: "#D2F0E6",
    icon: <MtIconWhite />,
    templateCode: "5a94a2e7-16cc-4943-9147-974acc4d1f96",
  },
  {
    name: "Charades",
    description: `Compete in teams. Act out smart, so your team can guess the word fast!`,
    tags: [
      { name: "Competitive", icon: <CrownIcon color="#fff" /> },
      { name: "Fun", icon: <SmileIcon /> },
    ],
    color: "--theme-lightBlue",
    textColor: "#CDEEFF",
    icon: <CharadesWhiteIcon />,
    templateCode: "be30f5a8-893f-43b6-bad1-ded5a2f89d14",
  },
  {
    name: "Know your team",
    description: `Get to know your teammates better over a longer 1-on-1 conversation.`,
    tags: [
      { name: "Team building", icon: <UsersIcon /> },
      { name: "Casual", icon: <CoffeeIcon /> },
    ],
    color: "--theme-orange",
    textColor: "#FBE5D4",
    icon: <KytIconWhite strokeColor="white" />,
    templateCode: "7b2117be-84ca-4757-bbb5-473be75c6f42",
  },
  {
    name: "Doodle race",
    description: `You draw, A.I. guesses. Can you out-doodle everyone else?`,
    tags: [
      { name: "Competitive", icon: <CrownIcon /> },
      { name: "A.I. Enabled", icon: <BotIcon /> },
    ],
    color: "--theme-yellow",
    textColor: "var(--theme-black)",
    icon: (
      <DoddleIcon
        strokeColor="var(--theme-yellow)"
        fillColor="var(--theme-black)"
      />
    ),
    theme: "BLACK",
    templateCode: "1de95999-8c13-4fb0-bd24-022d11e77887",
  },
  {
    name: "2 truths & a lie",
    description: `Three statements, one lie. Do you have the detective instincts?`,
    tags: [
      { name: "Team building", icon: <UsersIcon /> },
      { name: "Competitive", icon: <CrownIcon color="#fff" /> },
    ],
    color: "--theme-green",
    textColor: "#D2F0E6",
    icon: <TtalIcon strokeColor="var(--theme-green)" />,
    templateCode: "3f0b5ec9-a0e0-4d68-b3cb-4f5d80d13a38",
  },
  {
    name: "Trivia",
    description: `A game of quick wit, played in small groups. But we keep a global score.`,
    tags: [
      { name: "Competitive", icon: <CrownIcon color="#fff" /> },
      { name: "Fun", icon: <SmileIcon /> },
    ],
    color: "--theme-orange",
    textColor: "#FBE5D4",
    icon: <TriviaIcon strokeColor="var(--theme-orange)" />,
    templateCode: "8dc49860-81c7-4ef1-9b7a-abec105d455d",
  },
  {
    name: "Speed 1-on-1s",
    description: `Weather or traffic! Match up for quick chats over multiple speed rounds.`,
    tags: [
      { name: "Casual", icon: <CoffeeIcon /> },
      { name: "Fun", icon: <SmileIcon /> },
    ],
    color: "--theme-lightBlue",
    textColor: "#CDEEFF",
    icon: <SpeedIconWhite />,
    templateCode: "80947f8e-2e81-4514-9dfb-7b41462e062d",
  },
  {
    name: "Would you rather",
    description: `World’s toughest questions need an answer. Choose your side.`,
    tags: [
      { name: "Fun", icon: <SmileIcon /> },
      { name: "Casual", icon: <CoffeeIcon /> },
    ],
    color: "--theme-green",
    textColor: "#D2F0E6",
    icon: <WYRIcon strokeColor="var(--theme-green)" />,
    templateCode: "327b9e40-ae36-4352-b3da-a16a1641b2c6",
  },
]

const DEFAULT_SLIDER_VALUE = 15
const SLIDE_BY = 15

const MixersSection = ({ setIsModalVisible, title, subTitle }) => {
  const [isMobile, setIsMobile] = React.useState(false)
  const [slideValue, setSlideValue] = React.useState(0)
  const firstCardRef = React.useRef<HTMLDivElement>(null)
  const lastCardRef = React.useRef<HTMLDivElement>(null)
  const { width } = useWindowSize()
  const isFirstCardOnScreen = useOnScreen(firstCardRef, "HORIZONTAL")
  const isLastCardOnScreen = useOnScreen(lastCardRef, "HORIZONTAL")

  React.useEffect(() => {
    setIsMobile(width < 640)
  }, [width])

  return (
    <div className="mixers-section">
      <div className="h2Text">{title}</div>
      <div className="body-txt container-subtxt mixers-subtext">{subTitle}</div>
      <div
        className={`mixers-cards-container ${
          isMobile && "horizontal-scrollable"
        }`}
      >
        <div
          className="mixers-cards"
          style={{
            transform: `translateX(${
              (isMobile ? 0 : DEFAULT_SLIDER_VALUE) + slideValue
            }%)`,
          }}
        >
          {MIXERS.map((mixer, index) => (
            <MixerCard
              key={index}
              name={mixer.name}
              description={mixer.description}
              tags={mixer.tags}
              color={mixer.color}
              textColor={mixer.textColor}
              icon={mixer.icon}
              theme={mixer.theme}
              rotate={isMobile ? "0" : index % 2 === 0 ? "-3" : "3"}
              lastCardRef={lastCardRef}
              firstCardRef={firstCardRef}
              cardIndex={index}
              setIsModalVisible={setIsModalVisible}
              templateCode={mixer.templateCode}
            />
          ))}
        </div>
        {!isFirstCardOnScreen && !isMobile && (
          <div
            className="mixers-nav-btn mixers-left-btn"
            onClick={() => setSlideValue(val => val + SLIDE_BY)}
          >
            <RIghtArrow2 isLeft />
          </div>
        )}
        {!isLastCardOnScreen && !isMobile && (
          <div
            className="mixers-nav-btn mixers-right-btn"
            onClick={() => setSlideValue(val => val - SLIDE_BY)}
          >
            <RIghtArrow2 />
          </div>
        )}
      </div>
    </div>
  )
}

export default MixersSection
